// Personalisation helpers.
//
// The dashboard greeting must ALWAYS address the logged-in person by their own
// first name — never by the business name. Older provisioning paths sometimes
// stored a business-derived value (the full company name or its initials, e.g.
// "CTM" for "Computer Technology Matters") in users.first_name. This helper
// detects that corruption signature so the UI degrades to a neutral greeting
// instead of greeting someone by their company name.

const _norm = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');

// Candidate initial-strings for a business name, computed both from the raw
// words and after stripping legal suffixes, so both "Acme Trading Co" -> "ATC"
// and "Computer Technology Matters Pty Ltd" -> "CTM" are caught.
const _businessInitialVariants = (s) => {
  const base = (s || '').replace(/\([^)]*\)/g, ' ').replace(/[^a-zA-Z\s]/g, ' ').trim();
  const rawWords = base.split(/\s+/).filter(Boolean);
  const suffixes = new Set(['pty', 'ltd', 'inc', 'llc', 'co', 'group', 'holdings', 'the']);
  const strippedWords = rawWords.filter((w) => !suffixes.has(w.toLowerCase()));
  const variants = new Set();
  [rawWords, strippedWords].forEach((words) => {
    if (words.length) variants.add(words.map((w) => w[0]).join('').toLowerCase());
  });
  return variants;
};

/**
 * Returns the person's first name for a greeting, or '' when the stored value
 * is missing or is actually the business name / its initials.
 */
export function resolveGreetingName(firstName, businessName) {
  const fn = (firstName || '').trim();
  if (!fn) return '';
  const nfn = _norm(fn);
  const bn = _norm(businessName);
  if (bn) {
    if (nfn === bn) return '';                          // exact business name
    if (_businessInitialVariants(businessName).has(nfn)) return ''; // business initials (e.g. "ctm")
    if (bn.startsWith(nfn + ' ')) return '';            // leading word(s) of the business name
  }
  return fn.split(/\s+/)[0];
}

